import { useCallback, useEffect, useState } from 'react';
import { Content, ContentNode, ContentType } from '@amzn/awscat-pattern-library-service-model';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { Page } from '../../model/Page';
import { listNavigatorInlineTextFilters } from '../../services/ContentFindingFilters';
import { isThreadRoot } from '../../services/InlineCommentManager';
import { listPages, reorderChildPages } from '../../services/PageOperations';
import { isScannerComment } from '../../services/ScannerComments';
import { isSectionRenderable } from '../../services/SectionVisibility';
import { sectionIdForNode, sectionScrollTargetId } from '../v2-common/ContentNodeExtensions';
import { isCommentResolved } from '../v2-common/utils/commentStateUtils';
import { useContent } from '../v2-hooks/useContent';
import { useOpenInlineEdits } from '../v2-hooks/useOpenInlineEdits';
import { useCurrentSectionId, useCurrentTabId, useNavigateToSection } from '../v2-hooks/useUrlHash';

/** One entry in the navigator's list; may contain nested children. */
export interface NavigatorListItem {
  /** Reference to a specific node in the content being loaded in the editor. */
  readonly contentNode: ContentNode;
  /** True when this item's section id matches the current URL hash. */
  readonly isSelected: boolean;
  /** True when this item contains at least one unresolved scanner edit or comment. */
  readonly hasUnresolvedScanFindings: boolean;
  /** Items rendered beneath this one; empty when there are no children to show. */
  readonly children: readonly NavigatorListItem[];
}

/** Nothing to render — the editor service has not loaded a document. */
export interface ContentNavigatorHiddenViewModel {
  readonly status: 'hidden';
}

/** A document is loaded and its content tree can be rendered. */
export interface ContentNavigatorLoadedViewModel {
  readonly status: 'loaded';
  /** True when the document is open for editing. */
  readonly isEditMode: boolean;
  /** Content type of the loaded document. */
  readonly contentType: ContentType;
  /** Heading displayed above the list (e.g. "In this pattern"). */
  readonly heading: string;
  /** Top-level entries rendered by the navigator. */
  readonly topLevelListItems: readonly NavigatorListItem[];
  /** True when Guide pages can be reordered directly in the navigator. */
  readonly isPageReorderEnabled: boolean;
  /** Navigate the URL hash to `#contents?section=<id>` for the given item. */
  readonly onListItemSelected: (item: NavigatorListItem) => void;
  /** Reorder one parent's direct child pages. */
  readonly onPageOrderChanged: (parentPageId: string, newOrderIds: readonly string[]) => void;
}

export type ContentNavigatorViewModel =
  | ContentNavigatorHiddenViewModel
  | ContentNavigatorLoadedViewModel;

/**
 * Heading displayed above the navigator list, dependent on content type.
 * Throws for unsupported content types — callers should gate on this.
 */
function headingFor(contentType: ContentType): string {
  if (contentType === ContentType.Pattern) return 'In this pattern';
  if (contentType === ContentType.Guide) return 'In this guide';
  if (contentType === ContentType.Strategy) return 'In this strategy';
  throw new Error(`ContentNavigator does not support contentType: ${contentType}`);
}

/** Section id of the first visible top-level item for a document, or undefined. */
function firstSectionIdFor(content: Content, isEditMode: boolean): string | undefined {
  const { contentType } = content.contentMetadata;
  const { contentData } = content;
  if (contentType === ContentType.Pattern) {
    const first = visibleSections(contentData.contentSections ?? [], isEditMode)[0];
    return first ? sectionIdForNode(first) : undefined;
  }
  if (contentType === ContentType.Guide || contentType === ContentType.Strategy) {
    return sectionIdForNode(contentData);
  }
  return undefined;
}

/**
 * Pattern sections to list, dropping empties in view mode so the navigator
 * matches the read-only body. Edit mode keeps every section so authors can
 * still reach and fill them.
 */
function visibleSections(
  sections: readonly ContentNode[],
  isEditMode: boolean,
): readonly ContentNode[] {
  return isEditMode ? sections : sections.filter(isSectionRenderable);
}

/** Converts a ContentNode represents a section of a Pattern to a {@link NavigatorListItem}. */
function contentSectionToListItem(
  contentNode: ContentNode,
  selectedSectionId: string | undefined,
  isEditMode: boolean,
  hasUnresolvedScanFindings: (node: ContentNode) => boolean,
): NavigatorListItem {
  return {
    contentNode,
    isSelected: sectionIdForNode(contentNode) === selectedSectionId,
    hasUnresolvedScanFindings: hasUnresolvedScanFindings(contentNode),
    children: visibleSections(contentNode.contentSections ?? [], isEditMode).map((child) =>
      contentSectionToListItem(child, selectedSectionId, isEditMode, hasUnresolvedScanFindings),
    ),
  };
}

/** Converts a hierarchical Page into a NavigatorListItem with selection state. */
function pageToListItem(
  page: Page,
  selectedSectionId: string | undefined,
  hasUnresolvedScanFindings: (node: ContentNode) => boolean,
): NavigatorListItem {
  return {
    contentNode: page.node,
    isSelected: sectionIdForNode(page.node) === selectedSectionId,
    hasUnresolvedScanFindings: hasUnresolvedScanFindings(page.node),
    children: page.children.map((child) =>
      pageToListItem(child, selectedSectionId, hasUnresolvedScanFindings),
    ),
  };
}

/**
 * Build the top-level list items for the navigator from a loaded document.
 * Pattern → each `contentSections` entry (with its subContent as children);
 * empty sections are dropped in view mode. Guide/Strategy → `contentData`
 * (title only, no children) followed by each `subContent` entry (with its own
 * children). Other content types are not supported.
 */
function topLevelListItemsFor(
  content: Content,
  selectedSectionId: string | undefined,
  isEditMode: boolean,
  hasUnresolvedScanFindings: (node: ContentNode) => boolean,
): readonly NavigatorListItem[] {
  const { contentType } = content.contentMetadata;
  const { contentData } = content;

  if (contentType === ContentType.Pattern) {
    return visibleSections(contentData.contentSections ?? [], isEditMode).map((node) =>
      contentSectionToListItem(node, selectedSectionId, isEditMode, hasUnresolvedScanFindings),
    );
  }
  if (contentType === ContentType.Guide || contentType === ContentType.Strategy) {
    const rootItem: NavigatorListItem = {
      contentNode: contentData,
      isSelected: sectionIdForNode(contentData) === selectedSectionId,
      hasUnresolvedScanFindings: hasUnresolvedScanFindings(contentData),
      children: [],
    };
    const children = listPages(content).map((page) =>
      pageToListItem(page, selectedSectionId, hasUnresolvedScanFindings),
    );
    return [rootItem, ...children];
  }
  throw new Error(`ContentNavigator does not support contentType: ${contentType}`);
}

/**
 * Binds the navigator to the {@link ContentEditorService} loaded document
 * and exposes a click handler that updates the URL hash.
 */
export function useContentNavigatorViewModel(): ContentNavigatorViewModel {
  const service = useContentEditorService();
  const inlineCommentManager = useInlineCommentManager();
  const navigateToSection = useNavigateToSection();
  const currentSectionId = useCurrentSectionId();
  const currentTabId = useCurrentTabId();
  const content = useContent();
  const openedEdits = useOpenInlineEdits();
  const [, setCommentRevision] = useState(0);

  // When comments are resolved or refreshed, then rebuild navigator finding indicators.
  useEffect(() => {
    return inlineCommentManager.on('changed', () => {
      setCommentRevision((revision) => revision + 1);
    });
  }, [inlineCommentManager]);

  const hasUnresolvedScanFindings = useCallback(
    (node: ContentNode): boolean => {
      if (!content) return false;

      const filters = listNavigatorInlineTextFilters(content, node);
      return filters.some((filter) => {
        const hasUnresolvedEdit =
          openedEdits?.edits !== undefined &&
          service.filterInlineEdits(openedEdits.edits, filter).some((edit) => !edit.resolved);
        if (hasUnresolvedEdit) return true;

        return inlineCommentManager
          .filterComments(filter)
          .some(
            (comment) =>
              isThreadRoot(comment) &&
              isScannerComment(comment) &&
              !isCommentResolved(comment.state),
          );
      });
    },
    [content, inlineCommentManager, openedEdits, service],
  );

  const onListItemSelected = useCallback(
    (item: NavigatorListItem) => {
      const selectedSectionId = sectionIdForNode(item.contentNode);
      navigateToSection(selectedSectionId);

      // Re-selecting the already-current Contents section leaves the URL hash unchanged, so
      // useScrollToCurrentPatternSection won't refire; scroll here to cover that one case.
      if (currentTabId === 'contents' && currentSectionId === selectedSectionId) {
        document
          .getElementById(sectionScrollTargetId(selectedSectionId))
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [currentSectionId, currentTabId, navigateToSection],
  );

  const onPageOrderChanged = useCallback(
    (parentPageId: string, newOrderIds: readonly string[]) => {
      const currentContent = service.content;
      if (
        !service.isEditable ||
        currentContent?.contentMetadata.contentType !== ContentType.Guide
      ) {
        return;
      }

      const updated = reorderChildPages(currentContent, parentPageId, newOrderIds);
      if (updated) service.stageEdit(updated);
    },
    [service],
  );

  if (!content) return { status: 'hidden' };
  const loadedContentType = content.contentMetadata.contentType;
  const isEditMode = service.isEditable;
  const isPageReorderEnabled = isEditMode && loadedContentType === ContentType.Guide;
  // Default to the first top-level item when the URL hash has no section.
  const selectedSectionId = currentSectionId ?? firstSectionIdFor(content, isEditMode);
  return {
    status: 'loaded',
    isEditMode,
    contentType: loadedContentType,
    heading: headingFor(loadedContentType),
    isPageReorderEnabled,
    topLevelListItems: topLevelListItemsFor(
      content,
      selectedSectionId,
      isEditMode,
      hasUnresolvedScanFindings,
    ),
    onListItemSelected,
    onPageOrderChanged,
  };
}
